import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Monitor, 
  Apple, 
  Camera, 
  Mic, 
  Volume2, 
  Users, 
  ChevronRight, 
  ChevronLeft,
  X,
  Check,
  Download,
  Upload,
  Settings,
  Sparkles,
  Bell,
  Play,
  Square,
  Phone,
  PhoneOff
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

const RINGTONE_OPTIONS = [
  { id: 'none', label: 'Silent', freq1: 0, freq2: 0, pattern: 'none' },
  { id: 'classic', label: 'Classic Ring', freq1: 440, freq2: 480, pattern: 'double' },
  { id: 'modern', label: 'Modern Pulse', freq1: 523, freq2: 659, pattern: 'single' },
  { id: 'gentle', label: 'Gentle Chime', freq1: 587, freq2: 784, pattern: 'triple' },
  { id: 'urgent', label: 'Urgent', freq1: 880, freq2: 0, pattern: 'rapid' },
  { id: 'subtle', label: 'Subtle Tone', freq1: 392, freq2: 0, pattern: 'slow' },
];

const STEPS = [
  { id: 'welcome', title: 'Welcome!', icon: Sparkles },
  { id: 'desktop', title: 'Desktop App', icon: Download },
  { id: 'photo', title: 'Profile Photo', icon: Camera },
  { id: 'audio', title: 'Audio Settings', icon: Mic },
  { id: 'calls', title: 'Receive Calls', icon: Phone },
  { id: 'contacts', title: 'Contact Preferences', icon: Users },
  { id: 'complete', title: 'All Done!', icon: Check }
];

export default function NewUserWalkthrough({ open, onClose, userName }) {
  const { user, token, refreshUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [desktopVersions, setDesktopVersions] = useState({ windows: null, mac: null });
  
  // Profile photo state
  const [profileImage, setProfileImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  
  // Audio settings state
  const [audioDevices, setAudioDevices] = useState({ inputs: [], outputs: [] });
  const [selectedMic, setSelectedMic] = useState('default');
  const [selectedSpeaker, setSelectedSpeaker] = useState('default');
  
  // Ringtone state
  const [selectedRingtone, setSelectedRingtone] = useState('classic');
  const [previewingRingtone, setPreviewingRingtone] = useState(null);
  const previewAudioRef = useRef(null);
  
  // Contact preference state
  const [contactFilter, setContactFilter] = useState('internal');
  
  // Receive calls state
  const [dialerEnabled, setDialerEnabled] = useState(true);
  const [dialerToggleLoading, setDialerToggleLoading] = useState(false);
  
  // Detect if running in Electron
  const isElectron = typeof window !== 'undefined' && window.electronAPI?.isElectron;

  useEffect(() => {
    if (open) {
      fetchDesktopVersions();
      enumerateAudioDevices();
      // Load existing preferences
      const savedFilter = localStorage.getItem('defaultContactFilter');
      if (savedFilter) setContactFilter(savedFilter);
      const savedRingtone = user?.ringtone_preference || localStorage.getItem('preferredRingtone') || 'classic';
      setSelectedRingtone(savedRingtone);
      setDialerEnabled(user?.dialer_enabled !== false);
    }
    return () => stopPreview();
  }, [open]);

  const fetchDesktopVersions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/desktop/versions`);
      const versions = {};
      (response.data.versions || []).forEach(v => {
        versions[v.platform] = v;
      });
      setDesktopVersions(versions);
    } catch (error) {
      console.log('Could not fetch desktop versions');
    }
  };

  const enumerateAudioDevices = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter(d => d.kind === 'audioinput');
      const outputs = devices.filter(d => d.kind === 'audiooutput');
      setAudioDevices({ inputs, outputs });
      
      // Load saved preferences
      const savedMic = localStorage.getItem('preferredMicrophone');
      const savedSpeaker = localStorage.getItem('preferredSpeaker');
      if (savedMic) setSelectedMic(savedMic);
      if (savedSpeaker) setSelectedSpeaker(savedSpeaker);
    } catch (error) {
      console.log('Could not enumerate audio devices');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setUploadingImage(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        setProfileImage(base64);
        
        // Save to server
        await axios.put(`${API_URL}/api/auth/me`, 
          { profile_image: base64 },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        toast.success('Profile photo saved!');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const saveAudioSettings = async () => {
    localStorage.setItem('preferredMicrophone', selectedMic);
    localStorage.setItem('preferredSpeaker', selectedSpeaker);
    localStorage.setItem('preferredRingtone', selectedRingtone);
    
    // Save device labels to server (syncs across devices)
    const micDevice = audioDevices.inputs.find(d => d.deviceId === selectedMic);
    const speakerDevice = audioDevices.outputs.find(d => d.deviceId === selectedSpeaker);
    if (micDevice?.label) localStorage.setItem('preferredMicrophoneLabel', micDevice.label);
    if (speakerDevice?.label) localStorage.setItem('preferredSpeakerLabel', speakerDevice.label);
    
    try {
      await axios.put(`${API_URL}/api/auth/audio-preferences`, {
        preferred_mic_label: micDevice?.label || null,
        preferred_speaker_label: speakerDevice?.label || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.warn('Could not sync audio preferences to server');
    }
    
    // Save ringtone preference
    try {
      await axios.put(`${API_URL}/api/auth/ringtone`, {
        ringtone_id: selectedRingtone,
        custom_ringtone: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.warn('Could not sync ringtone preference to server');
    }
    
    toast.success('Audio settings saved!');
  };

  const stopPreview = () => {
    if (previewAudioRef.current) {
      try { previewAudioRef.current.close(); } catch (e) {}
      previewAudioRef.current = null;
    }
    setPreviewingRingtone(null);
  };

  const previewRingtone = (ringtone) => {
    stopPreview();
    if (ringtone.pattern === 'none') return;
    
    setPreviewingRingtone(ringtone.id);
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      previewAudioRef.current = ctx;
      
      const playTone = (startTime, freq, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(0.25, startTime + 0.03);
        gain.gain.linearRampToValueAtTime(0.25, startTime + dur - 0.05);
        gain.gain.linearRampToValueAtTime(0, startTime + dur);
        osc.start(startTime);
        osc.stop(startTime + dur);
      };

      const t = ctx.currentTime;
      if (ringtone.pattern === 'double') {
        playTone(t, ringtone.freq1, 0.4);
        if (ringtone.freq2) playTone(t, ringtone.freq2, 0.4);
        playTone(t + 0.5, ringtone.freq1, 0.4);
        if (ringtone.freq2) playTone(t + 0.5, ringtone.freq2, 0.4);
      } else if (ringtone.pattern === 'single') {
        playTone(t, ringtone.freq1, 0.6);
        if (ringtone.freq2) playTone(t + 0.2, ringtone.freq2, 0.4);
      } else if (ringtone.pattern === 'triple') {
        playTone(t, ringtone.freq1, 0.2);
        playTone(t + 0.25, ringtone.freq2 || ringtone.freq1, 0.2);
        playTone(t + 0.5, ringtone.freq1, 0.3);
      } else if (ringtone.pattern === 'rapid') {
        for (let i = 0; i < 4; i++) {
          playTone(t + i * 0.15, ringtone.freq1, 0.1);
        }
      } else if (ringtone.pattern === 'slow') {
        playTone(t, ringtone.freq1, 1.0);
      }

      setTimeout(() => {
        if (previewAudioRef.current === ctx) {
          try { ctx.close(); } catch (e) {}
          previewAudioRef.current = null;
        }
        setPreviewingRingtone(null);
      }, 1500);
    } catch (e) {
      setPreviewingRingtone(null);
    }
  };

  const saveContactPreference = () => {
    localStorage.setItem('defaultContactFilter', contactFilter);
    toast.success('Contact preference saved!');
  };

  const saveDialerSetting = async () => {
    setDialerToggleLoading(true);
    try {
      await axios.put(`${API_URL}/api/auth/profile`, 
        { dialer_enabled: dialerEnabled },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      if (refreshUser) await refreshUser();
    } catch (error) {
      console.warn('Could not save dialer setting');
    } finally {
      setDialerToggleLoading(false);
    }
  };

  const handleNext = () => {
    // Save settings for current step before moving
    if (STEPS[currentStep].id === 'audio') {
      saveAudioSettings();
    } else if (STEPS[currentStep].id === 'calls') {
      saveDialerSetting();
    } else if (STEPS[currentStep].id === 'contacts') {
      saveContactPreference();
    }
    
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    // Jump to complete step
    setCurrentStep(STEPS.length - 1);
  };

  const handleClose = () => {
    stopPreview();
    // Mark walkthrough as completed
    localStorage.setItem('walkthrough_completed', 'true');
    onClose();
  };

  const renderStepContent = () => {
    const step = STEPS[currentStep];
    
    switch (step.id) {
      case 'welcome':
        return (
          <div className="text-center py-6">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-primary" />
            </div>
            <h3 className="text-2xl font-bold mb-3">Welcome to CONNECT, {userName || 'there'}!</h3>
            <p className="text-muted-foreground mb-6">
              Let's get you set up in just a few quick steps. This will only take a minute.
            </p>
            <div className="flex justify-center gap-2 flex-wrap">
              {STEPS.slice(1, -1).map((s, i) => (
                <Badge key={s.id} variant="secondary" className="flex items-center gap-1">
                  <s.icon className="w-3 h-3" />
                  {s.title}
                </Badge>
              ))}
            </div>
          </div>
        );
        
      case 'desktop':
        return (
          <div className="py-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Download className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Get the Desktop App</h3>
              <p className="text-muted-foreground text-sm">
                For the best experience with auto-start, system tray, and instant call notifications.
              </p>
            </div>
            
            {isElectron ? (
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
                <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-green-600 font-medium">You're already using the desktop app!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {desktopVersions.windows?.download_url && (
                  <Button
                    variant="default"
                    className="w-full justify-center gap-2 bg-blue-600 hover:bg-blue-700"
                    onClick={() => window.open(desktopVersions.windows.download_url, '_blank')}
                  >
                    <Monitor className="w-5 h-5" />
                    Download for Windows
                    {desktopVersions.windows.version && (
                      <Badge variant="secondary" className="ml-2 bg-blue-500/30">
                        v{desktopVersions.windows.version}
                      </Badge>
                    )}
                  </Button>
                )}
                {desktopVersions.mac?.download_url && desktopVersions.mac.release_notes !== 'Coming soon' && (
                  <Button
                    variant="outline"
                    className="w-full justify-center gap-2"
                    onClick={() => window.open(desktopVersions.mac.download_url, '_blank')}
                  >
                    <Apple className="w-5 h-5" />
                    Download for Mac
                  </Button>
                )}
                <p className="text-xs text-center text-muted-foreground">
                  You can also download later from Profile → Desktop App
                </p>
              </div>
            )}
          </div>
        );
        
      case 'photo':
        return (
          <div className="py-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-500/10 flex items-center justify-center">
                <Camera className="w-8 h-8 text-purple-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Add a Profile Photo</h3>
              <p className="text-muted-foreground text-sm">
                Help your team recognize you at a glance.
              </p>
            </div>
            
            <div className="flex flex-col items-center gap-4">
              <Avatar className="w-24 h-24 border-4 border-primary/20">
                <AvatarImage src={profileImage || user?.profile_image} />
                <AvatarFallback className="text-2xl bg-primary/10">
                  {(userName || user?.name || 'U').charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
              
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
              >
                {uploadingImage ? (
                  <>Uploading...</>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {profileImage ? 'Change Photo' : 'Upload Photo'}
                  </>
                )}
              </Button>
              
              <p className="text-xs text-muted-foreground">
                You can change this anytime in Profile Settings
              </p>
            </div>
          </div>
        );
        
      case 'audio':
        return (
          <div className="py-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Mic className="w-8 h-8 text-emerald-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Audio Settings</h3>
              <p className="text-muted-foreground text-sm">
                Select your preferred microphone, speaker, and ringtone for calls.
              </p>
            </div>
            
            <div className="space-y-5 px-2">
              {/* Mic and Speaker in a responsive grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-medium">
                    <Mic className="w-4 h-4 text-emerald-500" />
                    Microphone
                  </Label>
                  <Select value={selectedMic} onValueChange={setSelectedMic}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select microphone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">System Default</SelectItem>
                      {audioDevices.inputs.map(device => (
                        <SelectItem key={device.deviceId} value={device.deviceId}>
                          {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-medium">
                    <Volume2 className="w-4 h-4 text-blue-500" />
                    Speaker
                  </Label>
                  <Select value={selectedSpeaker} onValueChange={setSelectedSpeaker}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select speaker" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">System Default</SelectItem>
                      {audioDevices.outputs.map(device => (
                        <SelectItem key={device.deviceId} value={device.deviceId}>
                          {device.label || `Speaker ${device.deviceId.slice(0, 8)}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <Bell className="w-4 h-4 text-amber-500" />
                  Ringtone
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {RINGTONE_OPTIONS.map(tone => (
                    <button
                      key={tone.id}
                      onClick={() => { setSelectedRingtone(tone.id); previewRingtone(tone); }}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left text-sm transition-all ${
                        selectedRingtone === tone.id
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'border-border hover:border-primary/40 text-muted-foreground'
                      }`}
                      data-testid={`ringtone-${tone.id}`}
                    >
                      {previewingRingtone === tone.id ? (
                        <Square className="w-3.5 h-3.5 text-primary shrink-0" />
                      ) : (
                        <Play className="w-3.5 h-3.5 shrink-0" />
                      )}
                      <span className="truncate">{tone.label}</span>
                      {selectedRingtone === tone.id && (
                        <Check className="w-3.5 h-3.5 text-primary ml-auto shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              <p className="text-xs text-center text-muted-foreground pt-2">
                You can change these anytime in Profile → Audio Settings
              </p>
            </div>
          </div>
        );
        
      case 'calls':
        return (
          <div className="py-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Phone className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Receive Calls</h3>
              <p className="text-muted-foreground text-sm">
                Would you like your softphone to ring when someone calls you?
              </p>
            </div>
            
            <div className="space-y-3 px-2">
              <div className="flex items-center justify-between p-4 rounded-lg border-2 transition-all"
                style={{ borderColor: dialerEnabled ? 'var(--primary)' : 'var(--border)' }}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${dialerEnabled ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                    {dialerEnabled ? (
                      <Phone className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <PhoneOff className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{dialerEnabled ? 'Calls Enabled' : 'Calls Disabled'}</p>
                    <p className="text-sm text-muted-foreground">
                      {dialerEnabled
                        ? 'Your softphone will ring for incoming calls'
                        : 'You can still monitor queues but will not receive calls'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={dialerEnabled}
                  onCheckedChange={setDialerEnabled}
                  disabled={dialerToggleLoading}
                  data-testid="walkthrough-dialer-toggle"
                />
              </div>
              
              <p className="text-xs text-center text-muted-foreground pt-2">
                You can change this anytime in Profile → Softphone
              </p>
            </div>
          </div>
        );
        
      case 'contacts':
        return (
          <div className="py-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Users className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Contact Preferences</h3>
              <p className="text-muted-foreground text-sm">
                Which contacts would you like to see first by default?
              </p>
            </div>
            
            <div className="space-y-3">
              {[
                { value: 'internal', label: 'Internal Contacts', desc: 'Team members and colleagues' },
                { value: 'external', label: 'External Contacts', desc: 'Customers and external contacts' },
                { value: 'all', label: 'All Contacts', desc: 'Show everyone together' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setContactFilter(option.value)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    contactFilter === option.value 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{option.label}</p>
                      <p className="text-sm text-muted-foreground">{option.desc}</p>
                    </div>
                    {contactFilter === option.value && (
                      <Check className="w-5 h-5 text-primary" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
        
      case 'complete':
        return (
          <div className="text-center py-6">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/10 flex items-center justify-center">
              <Check className="w-10 h-10 text-green-500" />
            </div>
            <h3 className="text-2xl font-bold mb-3">You're All Set!</h3>
            <p className="text-muted-foreground mb-6">
              Your account is ready to go. Start making calls!
            </p>
            <div className="p-4 rounded-lg bg-secondary/50 text-left">
              <p className="text-sm font-medium mb-2 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Need to change something later?
              </p>
              <p className="text-sm text-muted-foreground">
                You can update all these settings anytime from your <strong>Profile</strong> page.
                Click your avatar in the sidebar to access it.
              </p>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === STEPS.length - 1;
  const currentStepData = STEPS[currentStep];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl md:max-w-2xl" data-testid="new-user-walkthrough">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {!isFirstStep && !isLastStep && (
                <Badge variant="outline" className="font-mono">
                  {currentStep}/{STEPS.length - 2}
                </Badge>
              )}
            </div>
            {!isLastStep && (
              <Button variant="ghost" size="sm" onClick={handleSkip} className="text-muted-foreground">
                Skip setup
              </Button>
            )}
          </div>
        </DialogHeader>
        
        {/* Step content */}
        <div className="min-h-[300px]">
          {renderStepContent()}
        </div>
        
        {/* Navigation */}
        <div className="flex justify-between items-center pt-4 border-t">
          {!isFirstStep && !isLastStep ? (
            <Button variant="ghost" onClick={handlePrev}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          ) : (
            <div />
          )}
          
          {isLastStep ? (
            <Button onClick={handleClose} className="ml-auto">
              Get Started
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleNext}>
              {isFirstStep ? "Let's Go" : 'Next'}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
        
        {/* Step indicators */}
        {!isFirstStep && !isLastStep && (
          <div className="flex justify-center gap-1.5 pt-2">
            {STEPS.slice(1, -1).map((step, index) => (
              <div
                key={step.id}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index + 1 === currentStep 
                    ? 'bg-primary' 
                    : index + 1 < currentStep 
                      ? 'bg-primary/50' 
                      : 'bg-muted'
                }`}
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
